//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { getAgeFromCreatedAndCompleted } from '@/_utils/date';
import { getLineageMetaTitle } from '@/_utils/utils';
import { LineageMeta, OrderJobTask } from '@/redux/slices/slice_types';
import { Tooltip, Typography } from '@mui/material';
import {
  AssigneeSelect,
  DateTimeDisplay,
  History,
  Notes,
  OverflowMenu,
  PolarisIcon,
  PriorityNumber,
  ResultDisplay,
  TableStatus,
  TaskAction,
  TaskType
} from '../../../../exports/columnCommonComponents';

const columns: AllTableColumnsConfig<OrderJobTask> = {
  priority: {
    label: (
      <Tooltip title="Priority">
        <PolarisIcon icon="priority" height={20} style={{ verticalAlign: 'middle' }} />
      </Tooltip>
    ),

    minWidth: 50,
    render: (cell: OrderJobTask['priority']) => {
      return (
        <Tooltip title="Filter by Assignee to assign Priority">
          <div style={{ position: 'relative' }}>
            {/* negative margin and padding to force tooltip hover area to be larger */}
            {Number(cell) ? (
              <PriorityNumber priority={cell} />
            ) : (
              <div style={{ display: 'block', margin: -20, padding: 20 }}>
                <PolarisIcon icon="empty" height={10} style={{ marginLeft: 0 }} />
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
    draggable: (_: OrderJobTask['priority'], row: OrderJobTask) => row.status !== 'complete' && row.status !== 'cancelled' && row.assigned_user
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobTask['hid']) => <strong>{cell}</strong>
  },
  step: {
    label: 'Task Type',
    sortField: 'step',
    minWidth: 150,
    render: (cell: OrderJobTask['step'], row: OrderJobTask) => <TaskType type={cell} prettyName={row.pretty_name} />
  },
  job__due_date: {
    label: 'Due Date',
    minWidth: 100,
    render: (cell: OrderJobTask['job']['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at_dateformat: {
    sortField: 'created_at',
    label: 'Created',
    minWidth: 120,
    render: (_cell: OrderJobTask['created_at'], row: OrderJobTask) => <DateTimeDisplay>{row.created_at}</DateTimeDisplay>
  },
  updated_at: {
    sortField: 'updated_at',
    label: 'Last Updated',
    minWidth: 120,
    render: (_cell: OrderJobTask['updated_at'], row: OrderJobTask) => <DateTimeDisplay>{row.updated_at}</DateTimeDisplay>
  },
  profile: {
    label: 'Profile',
    minWidth: 300,
    render: (cell: OrderJobTask['profile']) => cell || '-'
  },
  created_at: {
    label: 'Age',
    minWidth: 120,
    render: (cell: OrderJobTask['created_at'], row: OrderJobTask) => (
      <div style={{ whiteSpace: 'nowrap' }}>{getAgeFromCreatedAndCompleted(row.created_at, row.completed_at)}</div>
    )
  },
  job__title__lineage_meta__series_name: {
    label: 'Title',
    render: (cell: LineageMeta['series_name'], row: OrderJobTask) =>
      !cell ? (
        row.job?.title?.name
      ) : (
        <>
          <Typography sx={{ minWidth: 250 }}>
            <strong>{cell}</strong>
          </Typography>
          <Typography variant="caption">{getLineageMetaTitle(row.job.title)}</Typography>
          {row.job.version?.name && (
            <>
              <br />
              <Typography variant="caption" sx={{ color: '#667799' }}>
                {row.job.version?.name}
              </Typography>
            </>
          )}
        </>
      )
  },
  job__version__external_id: {
    label: 'External Id'
  },
  extra_meta__accurate_result: {
    label: 'Result',
    minWidth: 150,
    render: (_: unknown, row: OrderJobTask) => <ResultDisplay result={row?.extra_meta?.accurate_result} remarks={row?.extra_meta?.accurate_remarks} />
  },
  status: {
    label: 'Status',
    minWidth: 120,
    render: (cell: OrderJobTask['status'], row: OrderJobTask) => (
      // only display status reason if status is failed or in_error
      <TableStatus status={cell} statusReason={row.status === 'failed' || row.status === 'in_error' ? row.status_reason : undefined} />
    )
  },
  assigned_user: {
    label: 'Assignee',
    minWidth: 135,
    render: (cell: any, row: OrderJobTask) => <AssigneeSelect value={cell} id={row.id} assigns="user" priority={row.priority} />,
    disableSort: true
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobTask) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} />
      </div>
    ),
    disableSort: true
  },
  action: {
    label: 'Action',
    disableSort: true,
    minWidth: 75,
    render: (_cell: any, row: OrderJobTask) => (
      <TaskAction task={row.assigned_user?.id == 27 ? { ...row, task_action: { type: 'packaging', action_url: '/packaging/1' } } : row} />
    )
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: OrderJobTask) => (
      <OverflowMenu>
        <History data={row} />
      </OverflowMenu>
    )
  }
};

export default columns;
